import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { api, isAbortError } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { classifyLoginError, formatThrottleWait, type LoginError } from '../../lib/authErrors';
import { takeAuthNotice } from '../../lib/authNotice';
import { AuthLayout } from '../auth/AuthLayout';
import {
  AuthErrorRegion,
  AuthPasswordField,
  AuthSubmit,
  AuthTextField,
} from '../auth/AuthPrimitives';

const ERROR_REGION_ID = 'login-error';

/**
 * The password sign-in screen.
 *
 * Two doors, one form. The email + password fields are always here; the
 * magic-link block below them renders only while `GET /api/auth/config` reports
 * the flag is still on, so flipping `MAGIC_LINK_ENABLED=false` deletes that
 * half of the screen server-side with no deploy here.
 */
export function LoginPage() {
  const { status, setSession } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  /** Live seconds remaining on a 429. Null whenever the error isn't a throttle. */
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  /** One-shot hand-off from whatever killed the last session (deactivation). */
  const [notice, setNotice] = useState<string | null>(null);
  const [magicLinkEnabled, setMagicLinkEnabled] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null);

  const errorRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // Read-and-clear the notice on mount. It belongs to exactly one render of
  // this page; a stale "your account was deactivated" next week would be worse
  // than no message at all.
  //
  // The ref guard is load-bearing, not defensive: StrictMode runs this effect
  // twice, and the second pass would read an already-consumed notice and set
  // state back to null — the message would be destroyed on the way to the
  // screen. Same shape as VerifyPage's single-use token guard.
  const noticeTaken = useRef(false);
  useEffect(() => {
    if (noticeTaken.current) return;
    noticeTaken.current = true;
    setNotice(takeAuthNotice());
  }, []);

  // Which sign-in doors are open. A failed or missing response is treated as
  // "the flag is absent", which §2 defines as today's behaviour — magic link
  // ON. Only the server explicitly saying `false` takes the block away.
  //
  // `magic_link_enabled` is snake_case, matching the shared contract. A
  // camelCase read here would be `undefined` forever, and `undefined !== false`
  // is `true` — the block would survive cutover and nobody would notice until
  // Joey clicked it and got a 410.
  useEffect(() => {
    const controller = new AbortController();
    api
      .authConfig({ signal: controller.signal })
      .then((cfg) => setMagicLinkEnabled(cfg.magic_link_enabled !== false))
      .catch((err: unknown) => {
        // An abort is OUR cancellation (StrictMode's simulated remount, or a
        // navigation) — not the server failing to answer. Treating it as a
        // failure would re-enable the magic-link block after the flag had
        // already told us to hide it.
        if (isAbortError(err)) return;
        setMagicLinkEnabled(true);
      });
    return () => controller.abort();
  }, []);

  // Move focus to the message on every failure — otherwise a screen-reader user
  // hears nothing actionable and a keyboard user has no idea the page changed.
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  // Live countdown for a 429. Ticks down to zero and then clears the error, so
  // the form is usable again the moment the server will accept it.
  useEffect(() => {
    if (secondsLeft === null) return;
    if (secondsLeft <= 0) {
      setError(null);
      setSecondsLeft(null);
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const fail = useCallback((err: unknown) => {
    const classified = classifyLoginError(err);
    setError(classified);
    setSecondsLeft(classified.kind === 'throttled' ? classified.retryAfterSeconds : null);
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // The button is never disabled (see AuthSubmit), so the guard lives here.
    if (submitting) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const session = await api.login(email.trim(), password);
      await setSession(session.sessionToken);
      navigate('/', { replace: true });
    } catch (err) {
      fail(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function onSendMagicLink() {
    if (submitting) return;
    // Native validation rather than our own copy: the browser already knows how
    // to say "fill this in" and how to say it in the user's language.
    const input = emailRef.current;
    if (input && typeof input.reportValidity === 'function' && !input.reportValidity()) return;

    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await api.requestMagicLink(email.trim());
      setLinkSentTo(email.trim());
    } catch (err) {
      fail(err);
    } finally {
      setSubmitting(false);
    }
  }

  // A session that is already good has no business looking at a login form.
  if (status === 'authenticated') return <Navigate to="/" replace />;

  if (linkSentTo !== null) {
    return (
      <AuthLayout title="Check your inbox" subtitle="Coffman Excavation · Time & Materials portal">
        <div className="auth-note is-positive">
          A sign-in link is on its way to <strong>{linkSentTo}</strong>. It expires in 15 minutes.
        </div>
        <div className="auth-links">
          <button
            type="button"
            className="btn-link"
            onClick={() => {
              setLinkSentTo(null);
              setPassword('');
            }}
          >
            Back to sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Sign in" subtitle="Coffman Excavation · Time & Materials portal">
      {notice ? <div className="auth-note">{notice}</div> : null}

      <form onSubmit={onSubmit} noValidate={false}>
        <div className="auth-fields">
          <AuthTextField
            ref={emailRef}
            label="Email"
            type="email"
            name="email"
            required
            autoFocus
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            readOnly={submitting}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={error ? ERROR_REGION_ID : undefined}
            placeholder="you@coffmanteam.com"
          />

          <AuthPasswordField
            label="Password"
            name="password"
            required
            autoComplete="current-password"
            readOnly={submitting}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-describedby={error ? ERROR_REGION_ID : undefined}
          />
        </div>

        {error ? (
          <AuthErrorRegion ref={errorRef} id={ERROR_REGION_ID}>
            {error.kind === 'throttled' ? (
              <>
                {/* This never names a lockout state. Saying so would confirm
                    the address has an account to anyone who can type one. */}
                Too many sign-in attempts. Try again in{' '}
                {formatThrottleWait(secondsLeft ?? error.retryAfterSeconds ?? 60)} — or{' '}
                <Link to="/forgot">reset your password by email</Link>.
              </>
            ) : (
              error.message
            )}
          </AuthErrorRegion>
        ) : null}

        <AuthSubmit busy={submitting} label="Sign in" busyLabel="Signing in…" />
      </form>

      <div className="auth-links">
        <Link className="btn-link" to="/forgot">
          Forgot your password?
        </Link>
      </div>

      {magicLinkEnabled ? (
        <>
          <div className="auth-divider">
            <hr />
            <span>or</span>
            <hr />
          </div>
          <button
            type="button"
            className="btn btn-ghost auth-alt"
            onClick={() => void onSendMagicLink()}
          >
            Email me a sign-in link instead
          </button>
        </>
      ) : null}
    </AuthLayout>
  );
}
